import { useEffect, useState, memo } from "react";
import FormErrors from "components/formParts/formErrors";
import { useTranslation } from "react-i18next";
import { decodeFirebaseError } from "utils/firebaseHelpers";
import { useNavigate } from "react-router-dom";
import { AccountPrivateInfoWrite, AccountPublicWrite, InputMaxLength } from "types";
import ToggleInput from "components/inputs/toggleInput";
import TextInput from "components/inputs/textInput";
import Button from "components/inputs/button";
import useLayout from "hooks/useLayout";
import useAuth from "hooks/useAuth";
import Form from "components/forms/form"
import { ReactComponent as SaveIcon } from "assets/icons/save.svg";
import ProcessingButton from "components/inputs/processingButton";
import { logger } from "utils/secureLogger";
import Section from "components/layout/section";
import Callout from "components/callout/callout";
import InputGroup from "@/components/inputs/InputGroup";

const AccountGeneralSettingsForm = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()

    // Shared.
    const [isBusiness, setIsBusiness] = useState<boolean>(false)
    const [phone, setPhone] = useState("")

    const [address, setAddress] = useState("")
    const [city, setCity] = useState("")
    const [addressState, setAddressState] = useState("")
    const [zip, setZip] = useState("")
    const [country, setCountry] = useState("")
    const [co, setCo] = useState("") // Care of

    // Business.
    const [orgName, setOrgName] = useState("")
    const [contactPerson, setContactPerson] = useState("")
    const [orgNum, setOrgNum] = useState("")
    const [vatNum, setVatNum] = useState("")

    // Individual.
    const [name, setName] = useState("")

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Auth hooks.
    const { updateAccountGeneralData, isUpdatingAccountGeneralData, accountPublicData, accountPrivateData } = useAuth()

    // Layout hooks.
    const { addToast } = useLayout()

    // Update the form fields with the user doc data.
    useEffect(() => {
        if (accountPublicData && accountPrivateData) {
            setIsBusiness(accountPrivateData.isBusiness ?? false)
            setPhone(accountPrivateData.contact?.phone || "")
            setAddress(accountPrivateData.postalAddress?.address || "")
            setCity(accountPrivateData.postalAddress?.city || "")
            setAddressState(accountPrivateData.postalAddress?.state || "")
            setZip(accountPrivateData.postalAddress?.zip || "")
            setCo(accountPrivateData.postalAddress?.country || "")
            if (accountPrivateData.isBusiness) {
                setOrgName(accountPrivateData.org?.orgName || "")
                setContactPerson(accountPrivateData.contact?.contactPerson || "")
                setOrgNum(accountPrivateData.org?.orgNum || "")
                setVatNum(accountPrivateData.org?.vatNum || "")
            } else {
                setName(accountPublicData.name || "")
            }
        }
    }, [accountPublicData, accountPrivateData])

    // Validate input.
    const validateInput = () => {
        // Clear old errors.
        setErrors([])
        // Check for empty fields.
        if (name === '') setErrors([...errors, 'Name is required'])
        if (isBusiness) {
            if (orgNum === '') setErrors([...errors, 'Organization number is required'])
        }
        if (errors.length > 0) return false
        return true
    }

    // Handle form submission.
    const handleSubmit = async (e: React.FormEvent) => {
        if (!validateInput()) return
        // Prevent the form from refreshing the page.
        e.preventDefault()
        setErrors([])
        
        try {
            // Generate the user doc data.
            const publicData = {
                name: name,
                other: {
                    avatarColor: accountPublicData?.other.avatarColor || '#3B82F6',
                },
            } as AccountPublicWrite
            const privateData = {
                isBusiness,
                org: {
                    orgName,
                    orgNum,
                    vatNum,
                },
                postalAddress: {
                    address,
                    city,
                    state: addressState,
                    zip,
                    co,
                    country,
                },
                contact: {
                    contactPerson,
                    phone,
                }
            } as AccountPrivateInfoWrite
            
            await updateAccountGeneralData({ publicData, privateData });
            addToast({ text: t('toastAccountUpdated'), colorScheme: 'green' })
            if (!accountPublicData?.other.isComplete) navigate('/storages')
        } catch (error: any) {
            logger.error('Account general settings form error', error, 'AccountGeneralSettings')
            const errorMessage = decodeFirebaseError(error?.code || error?.message || 'Unknown error')
            setErrors([errorMessage])
            addToast({ text: t('toastAccountError'), colorScheme: 'red' })
        }
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle orgName change.
    const handleOrgNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOrgName(e.target.value)
    }

    // Handle orgNum change.
    const handleOrgNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOrgNum(e.target.value)
    }

    // Handle vatNum change.
    const handleVatNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVatNum(e.target.value)
    }

    // Handle address change.
    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAddress(e.target.value)
    }

    // Handle city change.
    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCity(e.target.value)
    }

    // Handle address change.
    const handleAddressStateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAddressState(e.target.value)
    }

    // Handle zip change.
    const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setZip(e.target.value)
    }

    // Handle co change.
    const handleCoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCo(e.target.value)
    }

    // Handle isBusiness change.
    const handleIsBusinessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsBusiness(e.target.checked)
        if (!e.target.checked) {
            // Clear business fields when switching to individual.
            setOrgName("")
            setOrgNum("")
            setVatNum("")
            setContactPerson("")
        }
    }

    // Handle contactPerson change.
    const handleContactPersonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setContactPerson(e.target.value)
    }

    // Handle phone change.
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(e.target.value)
    }

    // Form JSX.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isUpdatingAccountGeneralData}
        >
            <Section>
                <Callout type="warning" title={t(`calloutcalloutAccountTypeImportantTitle`)}>{t(`calloutAccountTypeImportant`)}</Callout>
                <ToggleInput label={t('businessAccount')} checked={isBusiness} onChange={handleIsBusinessChange} />
                {!isBusiness && (
                    <TextInput label={t('name')} value={name || ''} onChange={handleNameChange} required maxLength={InputMaxLength.medium} />
                )}
                {isBusiness && (
                    <InputGroup>
                        <TextInput label={t(`labelOrganizationName`)} value={orgName || ''} onChange={handleOrgNameChange} required maxLength={InputMaxLength.medium} />
                        <TextInput label={t(`labelOrganizationNumber`)} value={orgNum || ''} onChange={handleOrgNumChange} required maxLength={InputMaxLength.medium} />
                        <TextInput label={t('labelVatNumber')} value={vatNum || ''} onChange={handleVatNumChange} maxLength={InputMaxLength.medium} />
                    </InputGroup>
                )}
            </Section>

            <Section>
                <InputGroup>
                    <TextInput label={t('address')} value={address || ''} onChange={handleAddressChange} autoComplete="address" maxLength={InputMaxLength.medium} />
                    <TextInput label={t('city')} value={city || ''} onChange={handleCityChange} autoComplete="city" maxLength={InputMaxLength.medium} />
                    <TextInput label={t('state')} value={addressState || ''} onChange={handleAddressStateChange} autoComplete="state" maxLength={InputMaxLength.medium} />
                    <TextInput label={t('zipCode')} value={zip || ''} onChange={handleZipChange} autoComplete="zip" maxLength={InputMaxLength.short} />
                    <TextInput label={t('country')} value={country || ''} onChange={(e) => setCountry(e.target.value)} autoComplete="country" maxLength={InputMaxLength.medium} />
                    <TextInput label={t('co')} value={co || ''} onChange={handleCoChange} autoComplete="co" maxLength={InputMaxLength.short} />
                </InputGroup>
                <InputGroup>
                    {isBusiness && (
                        <TextInput label={t(`contactPerson`)} value={contactPerson || ''} onChange={handleContactPersonChange} required maxLength={InputMaxLength.medium} />
                    )}
                    <TextInput label={t(`labelPhoneNumber`)} value={phone || ''} onChange={handlePhoneChange} maxLength={InputMaxLength.short} />
                </InputGroup>
            </Section>

            <div className={`input-holder`}>
                {isUpdatingAccountGeneralData ? (
                    <ProcessingButton design="primary" />
                ) : (
                    <Button wideOnMobile type="submit" design="primary"><SaveIcon />{accountPublicData?.other.isComplete ? t('save') : t('buttonCompleteAccount')}</Button>
                )}
            </div>
            {errors.length > 0 && <FormErrors errors={errors} />}
        </Form>
    )
}

export default memo(AccountGeneralSettingsForm)
